import * as yup from 'yup';
import { StringSchema } from 'yup';
import { FieldError, UseFormMethods } from 'react-hook-form';
import { parsePhoneNumber, validatePhoneNumberLength } from 'libphonenumber-js';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
// Constants
import { WEB_URL_REGEXP } from 'constants/auth';
import {
  EVENT_DESCRIPTION_MAX_LENGTH,
  EVENT_NAME_MAX_LENGTH,
  MAX_TICKET_TIER_CAPACITY,
  MIN_TICKET_PRICE,
  TICKET_TIER_LABEL_MAX_LENGTH,
} from 'constants/ticketEvents';
import {
  STREAM_DESCRIPTION_MAX_LENGTH,
  STREAM_NAME_MAX_LENGTH,
} from 'constants/streams';
// Helpers
import { parseToPlainText } from 'helpers/textEditor';
import { readWallClockInZone } from 'helpers/timeZone';
import {
  combineEventDateAndTime,
  CREATE_EVENT_STEP_FIELDS,
  eventSellsTickets,
  EventFormInputs,
  isEventAffiliateAvailable,
} from 'helpers/ticketEvents';

dayjs.extend(utc);
dayjs.extend(timezone);

type FormErrorsAndState<T> = Pick<UseFormMethods<T>, 'errors' | 'formState'>;

interface StringRequiredValidation {
  maxChars?: number;
  requiredLabel?: string;
}

const DEFAULT_MAX_CHARS_LENGTH = 256;

export const getMaxLengthErrMessage = (
  fieldName: string,
  maxChars = DEFAULT_MAX_CHARS_LENGTH
): string => `"${fieldName}" field is too long (max ${maxChars} char(s))`;

/** Rate rules shared by the stream default, Add Affiliate and Edit rate; empty is left to the caller. */
export const optionalAffiliateCommissionPercentValidation: StringSchema = yup
  .string()
  .matches(/^$|^\d{1,3}(?:\.\d{1,2})?$/, 'Only numbers are allowed')
  .test(
    'max-100',
    'Commission cannot exceed 100%',
    (value) => !value || Number(value) <= 100
  );

export const affiliateCommissionPercentValidation: StringSchema = optionalAffiliateCommissionPercentValidation.required(
  'Commission is required'
);

export const yupStringValidation = (fieldName: string): StringSchema => {
  return yup.string().max(256, getMaxLengthErrMessage(fieldName));
};

export const yupStringRequiredValidation = (
  fieldName: string,
  options?: StringRequiredValidation
): StringSchema => {
  const {
    maxChars = DEFAULT_MAX_CHARS_LENGTH,
    requiredLabel = `"${fieldName}" is required field.`,
  } = options || {};

  return yup
    .string()
    .trim() // ensuring that strings with only whitespaces are considered empty
    .max(maxChars, getMaxLengthErrMessage(fieldName, maxChars))
    .required(requiredLabel);
};

export const yupUrlStringValidation = (
  fieldName: string,
  options?: StringRequiredValidation
): StringSchema => {
  const { maxChars = DEFAULT_MAX_CHARS_LENGTH } = options || {};

  return yup
    .string()
    .test(
      'if-valid-url',
      `A "${fieldName}" link is badly formed or contains invalid characters.`,
      (val) => (val ? WEB_URL_REGEXP.test(val) : true)
    )
    .max(maxChars, getMaxLengthErrMessage(fieldName));
};

export const yupEditorStringRequiredValidation = (
  fieldName: string,
  requiredLabel?: string
): StringSchema => {
  return yup
    .string()
    .test('notEmpty', `"${fieldName}" is required field.`, (value) => {
      const parsedText = parseToPlainText(value);

      return !!parsedText;
    })
    .required(requiredLabel || `"${fieldName}" is required field.`);
};

export const yupEmailValidation = (): StringSchema => {
  return yup
    .string()
    .max(256, getMaxLengthErrMessage('Email'))
    .email('You should provide valid email address');
};

export const yupEmailRequiredValidation = (
  requiredLabel?: string
): StringSchema => {
  return yup
    .string()
    .trim()
    .max(256, getMaxLengthErrMessage('Email'))
    .email('You should provide valid email address')
    .required(requiredLabel || '"Email" is required field.');
};

export const yupInternationalPhoneValidation = (
  isOptional = false
): StringSchema => {
  if (isOptional) {
    return yup.string().test('phone', 'Invalid phone number', (value) => {
      if (!value) {
        return true;
      }

      const validate = validatePhoneNumberLength(value || '');
      if (validate === undefined) {
        const validatePhoneNumberResult = parsePhoneNumber(value || '');

        if (validatePhoneNumberResult && validatePhoneNumberResult.isValid()) {
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    });
  }

  return yup
    .string()
    .test('phone', 'Invalid phone number', (value) => {
      // check below will leave this test but right after go into the required() test which will display the message for empty field
      if (!value) {
        return true;
      }

      // function below returns a value if there is an issue(such as "TOO_SHORT") and returns undefined if there is no issue length-wise
      const validate = validatePhoneNumberLength(value || '');
      if (validate === undefined) {
        // function below returns a phone number object that contains all the number's information
        const validatePhoneNumberResult = parsePhoneNumber(value || '');

        // check below returns validity of the number tested
        if (validatePhoneNumberResult && validatePhoneNumberResult.isValid()) {
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    })
    .required('"Phone number" is a required field.');
};

export const isReady = <T>(
  name: keyof T,
  { errors, formState }: FormErrorsAndState<T>
): boolean => {
  const fieldError = errors?.[name] as FieldError;

  return Boolean(formState.dirtyFields[name] && !fieldError?.message);
};

const STREAM_PRICE_REGEXP = /^(?:0\.\d{0,2}[1-9]|(?!0)\d{1,}(?:\.\d{0,2}[0-9])?)$/;

const scheduleDateSchema = (isEdit?: boolean) =>
  yup
    .string()
    .typeError('Date is required')
    .required('Date is required')
    // The picker's filterTime only guards the dropdown: a past slot still
    // gets in by typing into the input, or by picking a valid time and then
    // switching to a time zone that puts it in the past.
    .test('is-future', 'Date and time must be in the future', function (value) {
      // Editing must stay possible once a stream has already started.
      if (isEdit || !value) {
        return true;
      }

      const tzName = this.parent?.timeZone?.tzName;

      if (!tzName) {
        return dayjs(value).isAfter(dayjs());
      }

      return readWallClockInZone(value, tzName).isAfter(dayjs());
    });

const timeZoneSchema = yup
  .object()
  .shape({
    label: yup.string(),
    tzCode: yup.string(),
    offset: yup.number(),
  })
  .typeError('Time Zone is required')
  .required('Time Zone is required');

const ticketsOn = (ticketsEnabled: boolean, isFree: boolean) =>
  ticketsEnabled && !isFree;

/** The venue tickets a paid stream can sell, checked only while Event Tickets is on. */
const streamTicketsValidation = {
  ticketsEnabled: yup.boolean(),
  venue: yup.object().when(['ticketsEnabled', 'isFree'], {
    is: ticketsOn,
    then: yup.object().shape({
      name: yup.string().trim().required('Venue is required'),
      addressLine1: yup.string().trim().required('Street address is required'),
      city: yup.string().required('Pick the full venue address from the list'),
      region: yup
        .string()
        .required('Pick the full venue address from the list'),
      postalCode: yup.string().trim().required('ZIP code is required'),
      country: yup
        .string()
        .required('Pick the full venue address from the list'),
      latitude: yup
        .number()
        .nullable()
        .required('Pick the venue address from the list'),
    }),
  }),
  doorsOpenTime: yup.mixed().when(['ticketsEnabled', 'isFree'], {
    is: ticketsOn,
    then: yup
      .mixed()
      .test('required', 'Door time is required', Boolean)
      .test(
        'before-start',
        'Doors must open before the stream starts',
        function (doorsOpenTime) {
          const { scheduleDate } = this.parent;

          if (!doorsOpenTime || !scheduleDate) {
            return true;
          }

          const doors = combineEventDateAndTime(
            dayjs(scheduleDate).toDate(),
            doorsOpenTime
          );

          return !dayjs(doors).isAfter(scheduleDate);
        }
      ),
  }),
  tiers: yup.array().when(['ticketsEnabled', 'isFree'], {
    is: ticketsOn,
    then: yup.array().min(1, 'Add at least one ticket tier'),
  }),
};

export const createEditStreamValidationSchema = (isEdit?: boolean) => {
  return yup.object().shape({
    scheduleDate: scheduleDateSchema(isEdit),
    name: yup
      .string()
      .max(STREAM_NAME_MAX_LENGTH, `Max ${STREAM_NAME_MAX_LENGTH} characters`)
      .required('Title is required'),
    description: yup
      .string()
      .max(
        STREAM_DESCRIPTION_MAX_LENGTH,
        `Max ${STREAM_DESCRIPTION_MAX_LENGTH} characters`
      )
      .required('Description is required'),
    timeZone: timeZoneSchema,
    isHidden: yup.boolean(),
    previewImage: yup.string().required('Poster is required'),
    image: yup.mixed(),
    affiliateEnabled: yup.boolean(),
    affiliateCommissionPercent: yup
      .string()
      .when(['affiliateEnabled', 'isFree'], {
        is: (affiliateEnabled: boolean, isFree: boolean) =>
          Boolean(affiliateEnabled) && !isFree,
        then: affiliateCommissionPercentValidation,
        otherwise: yup.string(),
      }),
    ...(!isEdit && {
      isFree: yup.boolean(),
      price: yup.string().when(['isFree'], {
        is: false,
        then: yup
          .string()
          .required('Price is required')
          .matches(STREAM_PRICE_REGEXP, 'Only numbers are allowed'),
        otherwise: yup.string(),
      }),
      ...streamTicketsValidation,
    }),
  });
};

export const createEventValidationSchema = (isEdit?: boolean) =>
  yup.object().shape({
    name: yup
      .string()
      .trim()
      .max(EVENT_NAME_MAX_LENGTH, `Max ${EVENT_NAME_MAX_LENGTH} characters`)
      .required('Event name is required'),
    description: yup
      .string()
      .max(
        EVENT_DESCRIPTION_MAX_LENGTH,
        `Max ${EVENT_DESCRIPTION_MAX_LENGTH} characters`
      )
      .required('Description is required'),
    previewImage: yup.string().required('Poster is required'),
    image: yup.mixed(),
    // The pickers clear to '', which mixed().required() lets through.
    startDate: yup.mixed().test('required', 'Date is required', Boolean),
    startTime: yup.mixed().test('required', 'Start time is required', Boolean),
    scheduleDate: scheduleDateSchema(isEdit),
    timeZone: timeZoneSchema,
    isRecurring: yup.boolean(),
    venue: yup.object().shape({
      name: yup.string().trim().required('Venue is required'),
      addressLine1: yup.string().trim().required('Address is required'),
      city: yup.string().trim().required('City is required'),
      region: yup.string().trim().required('State/Province is required'),
      postalCode: yup.string().trim().required('ZIP code is required'),
      country: yup.string().required('Country is required'),
      latitude: yup.number().nullable().required('Pin the venue on the map'),
    }),
    streamEnabled: yup.boolean(),
    isFree: yup.boolean(),
    price: yup.string().when(['streamEnabled', 'isFree'], {
      is: (streamEnabled: boolean, isFree: boolean) => streamEnabled && !isFree,
      then: yup
        .string()
        .required('Price is required')
        .matches(STREAM_PRICE_REGEXP, 'Only numbers are allowed'),
      otherwise: yup.string(),
    }),
    ticketsEnabled: yup.boolean(),
    tiers: yup.array().when(['streamEnabled', 'ticketsEnabled'], {
      is: (streamEnabled: boolean, ticketsEnabled: boolean) =>
        eventSellsTickets({ streamEnabled, ticketsEnabled }),
      then: yup.array().min(1, 'Add at least one ticket tier'),
    }),
    affiliateEnabled: yup.boolean(),
    affiliateCommissionPercent: yup
      .string()
      .when(['affiliateEnabled', 'streamEnabled', 'isFree'], {
        is: (
          affiliateEnabled: boolean,
          streamEnabled: boolean,
          isFree: boolean
        ) =>
          Boolean(affiliateEnabled) &&
          isEventAffiliateAvailable({ streamEnabled, isFree }),
        then: affiliateCommissionPercentValidation,
        otherwise: yup.string(),
      }),
  });

// Typed loosely, since the form values hold Dates and Files where the schema casts strings.
const CREATE_EVENT_SCHEMA: yup.Schema<unknown> = createEventValidationSchema();
const EDIT_EVENT_SCHEMA: yup.Schema<unknown> = createEventValidationSchema(
  true
);

/** Whether a wizard step's fields pass the event schema, checked quietly so no error shows yet. */
export const isEventStepComplete = (
  step: number,
  values: EventFormInputs,
  isEdit = false
): boolean => {
  const schema = isEdit ? EDIT_EVENT_SCHEMA : CREATE_EVENT_SCHEMA;

  return CREATE_EVENT_STEP_FIELDS[step - 1].every((field) => {
    try {
      schema.validateSyncAt(field, values);
      return true;
    } catch (error) {
      return !yup.ValidationError.isError(error);
    }
  });
};

export const ticketTierValidationSchema = yup.object().shape({
  label: yup
    .string()
    .trim()
    .max(
      TICKET_TIER_LABEL_MAX_LENGTH,
      `Max ${TICKET_TIER_LABEL_MAX_LENGTH} characters`
    )
    .required('Tier name is required'),
  price: yup
    .number()
    .typeError('Price is required')
    .min(MIN_TICKET_PRICE, `Minimum price is $${MIN_TICKET_PRICE.toFixed(2)}`)
    .test(
      'cents',
      'Use at most two decimals',
      (price) => price == null || Number.isInteger(+(price * 100).toFixed(6))
    )
    .required('Price is required'),
  capacity: yup
    .number()
    .typeError('Quantity is required')
    .integer('Use a whole number')
    .min(1, 'Sell at least 1 ticket')
    .max(MAX_TICKET_TIER_CAPACITY, 'Too many tickets for one tier')
    .required('Quantity is required'),
});

export const createAvailabilityTest = (
  fieldName: string,
  checkFn: (value: string | undefined) => Promise<boolean>
) => ({
  name: `is-available-${fieldName}`,
  message: `${fieldName} is already registered`,
  test: async (value: string | undefined) => {
    if (!value) return false;
    return await checkFn(value);
  },
});
